import * as gtag from '../../utils/gtag.js';
import { Layout } from '../../components/layout/';
import { GlobalPage } from '../../components/GlobalPage.jsx';
import { Article } from '../../components/articles/Article.jsx';
import { LoanReportForm } from '../../components/LoanReportForm.jsx';

function LendoLinkBox() {
  function onClick() {
    gtag.event({
      category: 'Lendo-klikk',
      action: 'Refinansiering',
    });
  }

  return (
    <a
      className="root"
      href="https://www.lendo.no/?utm_source=pengerno&utm_medium=refinansiering"
      onClick={onClick}
    >
      <h3>Refinansiere kredittkort- og forbruksgjeld?</h3>
      <span>Få tilbud fra opptil 16 banker med Lendo »</span>
      <style jsx>{`
        .root {
          display: block;
          margin: 32px;
          color: #000;
          text-decoration: none;
          cursor: default;
        }

        h3 {
          margin-top: 0;
          margin-bottom: 9px;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.2;
        }

        h3::before {
          display: block;
          content: 'Annonselenke';
          color: #777;
          font-size: 12px;
          margin-bottom: 0;
          font-weight: 500;
        }

        span {
          display: block;
          margin-top: 9px;
          font-weight: 600;
          line-height: 1.2;
          color: blue;
          cursor: pointer;
        }

        span:hover {
          text-decoration: underline;
        }

        @media screen and (min-width: 750px) {
          .root {
            margin: 0;
          }
        }
      `}</style>
    </a>
  );
}

export default function RefinancingPage() {
  return (
    <GlobalPage
      title="Dette må du vite om refinansiering av boliglån"
      description="Har du det beste boliglånet du kan få? Det kan være store summer å spare på å refinansiere boliglånet. Vår tjeneste skaper gjennomsiktighet i markedet og gjør deg bedre rustet til å ta opp kampen om lavere boligrente mot bankene. Prøv vår refinansieringskalkulator nå!"
      canonical="/boliglan/refinansiering"
    >
      <Layout>
        <Article
          headline="Dette må du vite om refinansiering av boliglån"
          leadText="Visste du at det kan være store summer å spare hver måned på å velge
            riktig bank?"
        >
          <p>
            Mange av oss dropper å sammenligne det som for mange er den største,
            månedlige utgiftsposten: boliglånsrenter. Historisk sett ligger
            renten rekordlavt og det gjør at det er fort å bli litt sløv når det
            kommer til å sjekke egne vilkår og stille krav til banken din. Det
            finnes hjelp for å få bedre rentevilkår – og det er mye enklere enn
            du tror.
          </p>
          <aside className="aside--larger">
            <LoanReportForm view="inline" />
          </aside>
          <p>
            Vi ser at låntakere stadig blir mer prisbevisste når det kommer til
            hvilke vilkår de har på boliglånet sitt. Økt fokus i media kombinert
            med rekordlave renter har trolig hjulpet på at stadig flere gjør en
            ekstra innsats for å sjekke at de ikke betaler mer enn nødvendig.
            Det er en utvikling vi liker, men det er fortsatt stort rom for
            forbedring.
          </p>
          <p>
            I en tid med lave boliglånsrenter, er det lett å glemme hvor mye vi
            faktisk betaler på boliglånet vårt hver måned. Det er andre ting som
            opptar de fleste av oss mer når det gjelder økonomi, fordi
            belønningen virker mer konkret. Har du tenkt over hvor mye tid du
            bruker på å planlegge ferier? Det er vanlig å bruke timesvis på å
            finne de billigste flybillettene og de beste hotellene til lavest
            mulig pris. Det finnes ikke{' '}
            <a href="https://www.dailymail.co.uk/news/article-2534814/Planning-holiday-really-takes-How-organising-trips-away-takes-longer-break-itself.html">
              undersøkelser
            </a>{' '}
            på tidsbruk for dette i Norge, men undersøkelser fra England viser
            at engelskmenn bruker i snitt 13 timer bare på å søke seg frem til
            hotellet de vil bruke til ferien. Vi nordmenn er kanskje ikke så
            veldig annerledes?
          </p>
          <p>
            Tenker du tilbake på forrige gang du kjøpte deg bolig vil du trolig
            kjenne deg igjen i at du hadde stor fokus på hvilken rente de ulike
            bankene kunne tilby deg. Banker som ligger marginalt høyere enn de
            andre avslås og etter en forhandlingsrunde sitter du igjen med det
            du anser å være det beste tilbudet. Etter at valget har falt på den
            banken som gir deg det beste tilbudet slår du deg til ro med at du
            har gjort en grundig jobb som har spart deg for unødvendige
            renteutgifter i årene fremover.
          </p>
          <h2>Har du det beste boliglånet du kan få i dag?</h2>
          <p>
            Tabben mange gjør skjer derimot i etterkant. En vanlig begrensning
            vi mennesker har er evnen til å kun fokusere på en ting om gangen.
            Etter man har begynt å betale på boliglånet sitt blir det en vane og
            noe man tenker «går av seg selv». Man glemmer derfor å ta en jevnlig
            sjekk av boliglånet sitt for å sjekke om det fortsatt er
            konkurransedyktig. I noen tilfeller kan man oppleve at banken man
            valgte har gått fra å være den billigste til blant de dyreste i
            markedet. En bank vil aldri gi deg beskjed om at de ikke lengre er
            like gode på pris som de engang var, så her må du selv følge med og
            gjøre jobben.
          </p>
          <p>
            Ved ikke å følge med på renten i fremtiden gjør du deg en
            bjørnetjeneste som fort kan koste deg dyrt. Det er lett å tenke at
            1.500 kroner i måneden ikke utgjør all verdens men i løpet av 5 år
            utgjør det 90.000 kroner.
          </p>
          <p>
            Det mange ikke er klar over, er at man kan spare betydelig større
            beløp ved å sammenligne boliglånet sitt med andre bankers vilkår
            jevnlig. Det høres kanskje tungvint og vanskelig ut, men det er det
            ikke. Setter du av noen få minutter til å sammenligne boliglånet
            ditt med andre banker på Penger.no, ser du raskt om du kan kutte
            månedsutgiftene dine. Da kan du jo bruke det du sparer på noe fint i
            stedet.
          </p>
          <p>
            Betaler du for mye for ditt boliglån? Sammenlign med andre i ditt
            område og få hjelp til å presse ned boliglånsrenten!
          </p>
          <h2>Forhandle din boliglånsrente</h2>
          <p>
            Når du møter banken for å forhandle rente på boliglånet ditt er det
            viktig å være godt forberedt. Her har dere ikke samme interesse!
          </p>
          <aside>
            <LendoLinkBox />
          </aside>
          <p>
            Før du skal begynne å forhandle, burde du vite hvilke faktorer
            bankene er opptatt av.
          </p>
          <ul>
            <li>
              <b>Lav risiko:</b> God betalingsvilje/evne, stabil lønn hos en
              stabil arbeidsgiver, stabil familiesituasjon, lavt lån i forhold
              til boligverdi, en bolig som ligger i et område med mange
              eiendomssalg er eksempler på ting som gjør at du fremstår som en
              lavere risiko og bedre kunde for banken.
            </li>
            <li>
              <b>Høyt potensiale:</b> Hvis du har en attraktiv jobb med mulighet
              for forfremmelse og høyere lønn, hvis du er ung, hvis du viser
              interesse for sparing, forsikring og andre produkter er
              potensialet for at banken vil tjene penger på deg større, og de
              vil strekke seg lengre for å beholde deg som kunde.
            </li>
          </ul>
          <p>
            Grunnleggende forhandlingsteori tilsier at du som forhandlingspart
            må vite hva som er dine grenser og hva som er motparten sine
            grenser. Kunnskap er makt i en forhandlingssituasjon. I de fleste
            renteforhandlinger er det banken som har makten, fordi de har full
            innsikt i markedet. Dette kan du gjøre noe med gjennom å legge inn
            informasjon om deg selv i renteoversikten vi har laget. Vår
            boliglånsrapport skaper gjennomsiktighet i markedet og gjør deg
            bedre rustet til å ta opp kampen om lavere boligrente mot bankene.
          </p>
        </Article>
      </Layout>
    </GlobalPage>
  );
}
